import { EGender } from '@enums/Gender';
import HttpClientError from '@errors/HttpClientError';
import BackendClient from '@services/utils/BackendClient';
import HttpClient from '@services/utils/HttpClient';
import WebClient from '@services/utils/WebClient';

import { type ICreateSessionInputData } from './dtos/createSession/InputData';
import { type ICreateSessionOutputData } from './dtos/createSession/OutputData';
import { type IGetSessionInfoOutputData } from './dtos/getSessionInfo/OutputData';
import { type ISendMailForgotPasswordSessionInputData } from './dtos/sendMailForgotPasswordSession/InputData';
import { type ISessionRepository } from './ISessionRepository';

interface ICreateBody {
  email: string;
  senha: string;
}
interface ICreateResponse {
  token: string;
}

interface IBackendSessionInfo {
  access_granted: boolean;
  access_granted_date: string | null;
  area_activity: string | null;
  avatar: string | null;
  city_activity: string | null;
  country: string | null;
  created_at: string;
  document: string | null;
  email: string | null;
  gender: EGender | null;
  gpchat_balance: number;
  gpchat_beta_granted: boolean;
  gpchat_granted: boolean;
  gpchat_tokens: number;
  id: number;
  name: string | null;
  phone: string | null;
  plan: string | null;
  state_activity: string | null;
  year_graduation: string | null;
}

interface ISendMailForgotPasswordBody {
  email: string;
}

class SessionService implements ISessionRepository {
  public async create(
    inputData: ICreateSessionInputData,
  ): Promise<ICreateSessionOutputData> {
    const response = await HttpClient.post<ICreateResponse, ICreateBody>({
      path: 'sessoes.php',
      body: {
        email: inputData.email,
        senha: inputData.senha,
      },
    });
    HttpClient.setBearerToken(response.data.token);
    BackendClient.setBearerToken(response.data.token);
    WebClient.setBearerToken(response.data.token);
    const output: ICreateSessionOutputData = {
      token: response.data.token,
    };
    return output;
  }

  public async getInfo(): Promise<IGetSessionInfoOutputData> {
    try {
      const response = await BackendClient.get<IBackendSessionInfo>({
        path: 'sessions',
      });
      const output: IGetSessionInfoOutputData = {
        accessGranted: response.data.access_granted,
        accessGrantedDate: response.data.access_granted_date,
        email: response.data.email || '',
        gender: response.data.gender!,
        gpchatBalance: response.data.gpchat_balance,
        gpchatBetaGranted: response.data.gpchat_beta_granted,
        gpchatGranted: response.data.gpchat_granted,
        gpchatTokens: response.data.gpchat_tokens,
        id: response.data.id,
        name: response.data.name || '',
        occupationArea: response.data.area_activity || '',
        occupationCity: response.data.city_activity,
        occupationState: response.data.state_activity,
        phone: response.data.phone || '',
        picture: response.data.avatar,
        plan: null,
        yearGraduated: response.data.year_graduation || '',
        createdAt: response.data.created_at,
        document: response.data.document,
        country: response.data.country,
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async sendMailForgotPassword(
    inputData: ISendMailForgotPasswordSessionInputData,
  ): Promise<void> {
    await HttpClient.post<unknown, ISendMailForgotPasswordBody>({
      path: 'recuperarsenha.php',
      body: {
        email: inputData.email,
      },
    });
  }
}

export default new SessionService();
